/**
 * Persisted rows -> view models.
 *
 * The pipeline writes camelCase objects inside jsonb columns and snake_case for
 * table columns, and the client types are a narrow mirror of the server schemas.
 * "Narrow" is the whole reason this file exists rather than a cast: a few fields
 * must be DROPPED or WITHHELD on the way out, and a cast would carry them.
 *
 * THREE RULES.
 *
 *   A MALFORMED RECORD BECOMES NULL, NEVER A PARTIAL ONE. Coverage drives the
 *   limitations band, which states how this run's search was narrower than
 *   intended. A half-parsed record would render a band computed from missing
 *   counts — a false statement about the search, which is worse than no band.
 *   Callers detect "column present but unparseable" by testing the column and
 *   the result, and report it; these functions stay pure so tests need no
 *   error-reporting stubs.
 *
 *   A LICENCE THAT FORBIDS DISPLAY IS ENFORCED HERE. Evidence may corroborate
 *   inside the validator and still not be republishable. The excerpt and the
 *   link are removed at the boundary rather than passed along with a flag,
 *   because a flag is only as good as every component that remembers to read it.
 *
 *   A WITHHELD VALUE STAYS VISIBLE AS WITHHELD. A field the founder chose not to
 *   share has no `value` (see supabase/functions/_shared/strategic-analysis/
 *   persisted-context.ts). Dropping the entry would render "provided, not
 *   shared" as "never provided" — a different fact about the company.
 */

import { COVERAGE_ELEMENT_STATES } from './types';
import type { DerivationRecord } from '@/components/strategic-analysis/MethodologyModal';
import type { InputsUsedSnapshot } from '@/components/strategic-analysis/InputsUsedModal';
import type {
  ClaimSourceLink,
  ClaimSourceRole,
  ClaimView,
  EvidenceView,
  ProvenancedInput,
  ResearchCoverageView,
} from './types';

type Row = Record<string, unknown>;

function isObject(value: unknown): value is Row {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function num(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function str(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/** Every key must be a finite number, or the whole record is unusable. */
function counts<K extends string>(value: unknown, keys: readonly K[]): Record<K, number> | null {
  if (!isObject(value)) return null;
  const out = {} as Record<K, number>;
  for (const key of keys) {
    const parsed = num(value[key]);
    if (parsed === null) return null;
    out[key] = parsed;
  }
  return out;
}

const TOTAL_KEYS = ['planned', 'searched', 'supported', 'conflicted', 'unknown', 'skipped'] as const;

/**
 * Totals the server only started emitting recently.
 *
 * Historical rows predate them, so an ABSENT value defaults to zero. A PRESENT
 * but malformed value is still a decode failure — "old row" and "corrupt row"
 * are different problems and defaulting both hides the second.
 */
const LEGACY_OPTIONAL_TOTAL_KEYS = ['retrieved', 'policyLimited'] as const;

function optionalCounts<K extends string>(
  value: unknown,
  keys: readonly K[],
): Record<K, number> | null {
  if (!isObject(value)) return null;
  const out = {} as Record<K, number>;
  for (const key of keys) {
    if (value[key] === undefined) {
      out[key] = 0;
      continue;
    }
    const parsed = num(value[key]);
    if (parsed === null) return null;
    out[key] = parsed;
  }
  return out;
}
const PRIMARY_KEYS = ['targeted', 'achieved'] as const;
const COUNTER_KEYS = ['targeted', 'completed', 'noneFound', 'limited'] as const;

function toProviderWarnings(value: unknown): ResearchCoverageView['providerWarnings'] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((raw) => {
    if (!isObject(raw)) return [];
    const providerId = str(raw.providerId);
    const message = str(raw.message);
    if (!providerId || !message) return [];
    return [{
      providerId,
      code: str(raw.code) ?? 'unknown',
      message,
      affectedElements: Array.isArray(raw.affectedElements)
        ? raw.affectedElements.filter((e): e is string => typeof e === 'string')
        : [],
    }];
  });
}

/**
 * `research_coverage` holds the whole server record; `provider_warnings` holds
 * the same array in its own column. The RPC can update the column on a later
 * write (`COALESCE(p_provider_warnings, provider_warnings)`), so an array there
 * wins — including an empty one, which legitimately means "no warnings".
 */
export function toCoverageView(
  coverage: unknown,
  providerWarnings: unknown,
): ResearchCoverageView | null {
  if (!isObject(coverage)) return null;

  const required = counts(coverage.totals, TOTAL_KEYS);
  const legacy = optionalCounts(coverage.totals, LEGACY_OPTIONAL_TOTAL_KEYS);
  const totals = required && legacy ? { ...required, ...legacy } : null;
  const primarySourceCoverage = counts(coverage.primarySourceCoverage, PRIMARY_KEYS);
  const counterEvidenceCoverage = counts(coverage.counterEvidenceCoverage, COUNTER_KEYS);
  const rounds = num(coverage.rounds);
  const searchCalls = num(coverage.searchCalls);
  const independenceGroups = num(coverage.independenceGroups);
  const retrievedAt = str(coverage.retrievedAt);

  if (
    !totals || !primarySourceCoverage || !counterEvidenceCoverage ||
    rounds === null || searchCalls === null || independenceGroups === null ||
    !retrievedAt || !Array.isArray(coverage.elements)
  ) {
    return null;
  }

  // STRICT, whole-record. One malformed element invalidates the coverage record
  // rather than quietly shrinking it.
  if (!Array.isArray(coverage.elements)) return null;
  const decodedElements = coverage.elements.map((raw): ResearchCoverageView['elements'][number] | null => {
    if (!isObject(raw)) return null;
    const methodologyElement = str(raw.methodologyElement);
    const planned = num(raw.planned);
    const searched = num(raw.searched);
    // VALIDATED against the vocabulary, not cast. A cast let an unrecognised
    // server state through as a label the UI rendered blank.
    const rawState = str(raw.state);
    const state = COVERAGE_ELEMENT_STATES.find((candidate) => candidate === rawState);
    // NULL, NOT AN EMPTY ARRAY. `flatMap` returning `[]` silently turned a
    // corrupted coverage record into an apparently valid PARTIAL one — the file
    // said "a malformed record becomes null, never a partial one" and did the
    // opposite. A reader cannot tell a run that searched two elements from a run
    // that searched five and lost three in transit.
    if (!methodologyElement || planned === null || searched === null || !state) return null;
    return { methodologyElement, planned, searched, state };
  });
  if (decodedElements.some((e) => e === null)) return null;
  const elements = decodedElements as ResearchCoverageView['elements'];

  // THE TOTALS MUST DESCRIBE THESE ELEMENTS. A record whose counts disagree with
  // its own rows is corrupt, and rendering it would put a number on screen that
  // nothing in the report supports. `planned` and `searched` are operation
  // counts and are deliberately outside the sum.
  const stateSum = totals.supported + totals.conflicted + totals.retrieved +
    totals.policyLimited + totals.unknown + totals.skipped;
  if (stateSum !== elements.length) return null;

  return {
    elements,
    totals,
    rounds,
    searchCalls,
    independenceGroups,
    primarySourceCoverage,
    counterEvidenceCoverage,
    providerWarnings: toProviderWarnings(
      Array.isArray(providerWarnings) ? providerWarnings : coverage.providerWarnings,
    ),
    budgetExhausted: coverage.budgetExhausted === true,
    retrievedAt,
  };
}

export function toDerivation(derivation: unknown): DerivationRecord | null {
  if (!isObject(derivation)) return null;
  return {
    methodologyVersion: str(derivation.methodologyVersion),
    controlledElements: Array.isArray(derivation.controlledElements)
      ? derivation.controlledElements.filter((e): e is string => typeof e === 'string')
      : undefined,
    thresholds: Array.isArray(derivation.thresholds)
      ? derivation.thresholds.flatMap((t) => {
        if (!isObject(t)) return [];
        const label = str(t.label);
        if (!label) return [];
        return [{ label, value: String(t.value ?? '') }];
      })
      : undefined,
    transformations: Array.isArray(derivation.transformations)
      ? derivation.transformations.filter((t): t is string => typeof t === 'string')
      : undefined,
    fallbackReason: str(derivation.fallbackReason),
    freshness: str(derivation.freshness),
    explanation: str(derivation.explanation),
  };
}

/**
 * The run's bound versions and the context it read.
 *
 * Context entries pass through as stored. An entry with no `value` is a field
 * the founder withheld, and it must reach the view WITHOUT a `value` key at all
 * — assigning `undefined` would make `'value' in entry` true and let a renderer
 * treat it as an empty value rather than a withheld one.
 */
export function toSnapshot(run: {
  input_snapshot?: unknown;
  methodology_version?: unknown;
  completed_at?: unknown;
}): InputsUsedSnapshot | null {
  const snapshot = run.input_snapshot;
  if (!isObject(snapshot)) return null;

  const strategyContextVersionId = str(snapshot.strategyContextVersionId);
  const strategyContextVersion = num(snapshot.strategyContextVersion);
  if (!strategyContextVersionId || strategyContextVersion === null) return null;

  const rawContext = isObject(snapshot.context) ? snapshot.context : {};
  const context: Record<string, ProvenancedInput> = {};
  for (const [fieldKey, entry] of Object.entries(rawContext)) {
    if (!isObject(entry)) continue;

    // Rebuilt rather than cast. `sourceKeys` is indexed directly by the
    // inputs-used view, so an entry missing it would throw at render time — and
    // a cast would have promised it was there.
    const base = {
      originLabel: (str(entry.originLabel) ?? 'profile') as ProvenancedInput['originLabel'],
      verificationLabel: (str(entry.verificationLabel) ?? 'extracted') as ProvenancedInput['verificationLabel'],
      sourceKeys: Array.isArray(entry.sourceKeys)
        ? entry.sourceKeys.filter((k): k is string => typeof k === 'string')
        : [],
      detailAvailable: entry.detailAvailable === true,
      founderConfirmed: entry.founderConfirmed === true,
      founderConfirmedAt: str(entry.founderConfirmedAt) ?? null,
      conflictDisposition: (str(entry.conflictDisposition) ?? 'none') as ProvenancedInput['conflictDisposition'],
      publicationScope: (str(entry.publicationScope) ?? 'founder_private') as ProvenancedInput['publicationScope'],
    };

    // Conditional spread, never `value: entry.value`: assigning undefined would
    // make `'value' in entry` true and turn a withheld field into an empty one.
    // The default scope above is the closed one for the same reason — an entry
    // with no marker is treated as not publishable.
    context[fieldKey] = 'value' in entry ? { ...base, value: entry.value } : base;
  }

  return {
    strategyContextVersion,
    strategyContextVersionId,
    marketAssumptionVersion: num(snapshot.marketAssumptionVersion),
    exitThesisVersion: num(snapshot.exitThesisVersion),
    methodologyVersion: str(snapshot.methodologyVersion) ?? str(run.methodology_version) ?? 'unknown',
    generatedAt: str(run.completed_at) ?? null,
    context,
  };
}

/**
 * The immutable version ids a run was bound to.
 *
 * Separate from `toSnapshot` because `InputsUsedSnapshot` is the MODAL's type —
 * it shows version NUMBERS to a reader. These are the ids used to fetch the
 * exact assumption and thesis rows the report read, which is a different job and
 * should not push storage identifiers into a view model.
 */
export function toBoundVersionIds(run: { input_snapshot?: unknown }): {
  strategyContextVersionId: string | null;
  marketAssumptionVersionId: string | null;
  exitThesisVersionId: string | null;
} {
  const snapshot = isObject(run.input_snapshot) ? run.input_snapshot : {};
  return {
    strategyContextVersionId: str(snapshot.strategyContextVersionId) ?? null,
    marketAssumptionVersionId: str(snapshot.marketAssumptionVersionId) ?? null,
    exitThesisVersionId: str(snapshot.exitThesisVersionId) ?? null,
  };
}

/**
 * Claims, with their evidence ids resolved through the join table.
 *
 * The claims table stores no evidence ids — `publish_strategic_analysis_run`
 * writes `detail` only, and the links live in `strategic_analysis_claim_sources`
 * keyed on the source ROW id, which then resolves to the citable `evidence_id`.
 * Every `role` is included: a `weakens` link is contrary evidence deliberately
 * kept attached to a published conclusion.
 */
export function toClaimViews(
  claims: unknown,
  joins: unknown,
  sources: unknown,
): ClaimView[] {
  if (!Array.isArray(claims)) return [];

  const evidenceIdBySourceId = new Map<string, string>();
  if (Array.isArray(sources)) {
    for (const raw of sources) {
      if (!isObject(raw)) continue;
      const id = str(raw.id);
      const evidenceId = str(raw.evidence_id);
      if (id && evidenceId) evidenceIdBySourceId.set(id, evidenceId);
    }
  }

  const linksByClaimId = new Map<string, ClaimSourceLink[]>();
  if (Array.isArray(joins)) {
    for (const raw of joins) {
      if (!isObject(raw)) continue;
      const claimId = str(raw.claim_id);
      const sourceId = str(raw.source_id);
      if (!claimId || !sourceId) continue;
      const evidenceId = evidenceIdBySourceId.get(sourceId);
      // A join whose source row is absent resolves to nothing. Emitting an
      // undefined id would produce a citation that resolves to no source.
      if (!evidenceId) continue;
      // PARSED, NEVER CAST. The role decides what the modal tells a reader about
      // a source; an unrecognised string read as `supports` because a cast said
      // so is the failure this whole boundary exists to prevent. Anything
      // outside the vocabulary is `context` — attached and visible, asserting
      // nothing.
      const role = parseClaimSourceRole(raw.role);
      const list = linksByClaimId.get(claimId) ?? [];
      if (!list.some((l) => l.evidenceId === evidenceId && l.role === role)) {
        list.push({ evidenceId, role });
      }
      linksByClaimId.set(claimId, list);
    }
  }

  return claims.flatMap((raw): ClaimView[] => {
    if (!isObject(raw)) return [];
    const claimKey = str(raw.claim_key);
    const statement = str(raw.statement);
    const methodologyElement = str(raw.methodology_element);
    if (!claimKey || !statement || !methodologyElement) return [];

    const id = str(raw.id);
    const links = (id ? linksByClaimId.get(id) : undefined) ?? [];
    return [{
      claimKey,
      kind: parseEnum(raw.kind, CLAIM_KINDS, 'unknown'),
      statement,
      supportState: parseEnum(raw.support_state, SUPPORT_STATES, 'not_verifiable'),
      material: raw.material === true,
      methodologyElement,
      links,
      evidenceIds: links.map((l) => l.evidenceId),
      counterEvidenceStatus: parseEnum(raw.counter_evidence_status, COUNTER_EVIDENCE_STATUSES, 'not_required'),
    }];
  });
}

/**
 * Retained sources, as a permitted reader may see them.
 *
 * RLS has already withheld rows an investor may not read at all. This enforces
 * the second, independent axis: a row that may be READ but whose licence forbids
 * DISPLAY keeps its attribution and loses its excerpt and link.
 */
export function toEvidenceViews(sources: unknown): EvidenceView[] {
  if (!Array.isArray(sources)) return [];

  return sources.flatMap((raw): EvidenceView[] => {
    if (!isObject(raw)) return [];
    const evidenceId = str(raw.evidence_id);
    if (!evidenceId) return [];

    // Absent or malformed licence => not displayable. The permissive default is
    // the one that republishes material nobody licensed.
    const licence = isObject(raw.license_policy) ? raw.license_policy : null;
    const canDisplay = licence?.display === true;
    // SHOWING A LINK AND SHOWING THE SOURCE'S OWN WORDS ARE DIFFERENT
    // PERMISSIONS, and the excerpt used to be gated on the link one. A source
    // whose prose may not be shown is still a fully visible citation — publisher,
    // title, dates and a link the reader can follow — and the conservative
    // default is exactly that shape. Under it, every open-web source would have
    // had its stored prose displayed.
    //
    // `=== true` so a row written before this permission was recorded reads as
    // NOT PERMITTED. An absent grant and a granted one are indistinguishable
    // otherwise, and only one of those two readings republishes prose nobody
    // licensed. It also requires `display`: prose cannot be shown by a licence
    // that forbids showing the source at all.
    const canShowExcerpt = canDisplay && licence?.excerptDisplay === true;

    return [{
      evidenceId,
      title: str(raw.title),
      publisher: str(raw.publisher),
      canonicalUrl: canDisplay ? str(raw.canonical_url) : undefined,
      publishedAt: str(raw.published_at),
      retrievedAt: str(raw.retrieved_at),
      excerpt: canShowExcerpt ? str(raw.excerpt) : undefined,
      sourceQuality: (str(raw.source_quality) ?? 'unclassified') as EvidenceView['sourceQuality'],
      independenceGroup: str(raw.independence_group) ?? evidenceId,
      canDisplay,
    }];
  });
}

// ---------------------------------------------------------------------------
// Strict vocabulary parsing
// ---------------------------------------------------------------------------
//
// EVERY ENUM ON THIS BOUNDARY WAS A CAST. `str(raw.kind) as ClaimView['kind']`
// tells the compiler a database string is a member of a closed set and checks
// nothing, so a value nobody registered — a typo, a column read from the wrong
// row, a future enum this build predates — reached a component that switched on
// it and fell through to whichever branch happened to be last.

const CLAIM_SOURCE_ROLES: readonly ClaimSourceRole[] = ['supports', 'weakens', 'conflicts', 'context'];
const CLAIM_KINDS: readonly ClaimView['kind'][] = [
  'fact',
  'calculation',
  'estimate',
  'inference',
  'point_to_consider',
  'unknown',
];
const SUPPORT_STATES: readonly ClaimView['supportState'][] = [
  'supported',
  'partially_supported',
  'conflicted',
  'unsupported',
  'not_verifiable',
];
const COUNTER_EVIDENCE_STATUSES: readonly ClaimView['counterEvidenceStatus'][] = [
  'completed_with_results',
  'none_found_after_search',
  'provider_failed',
  'filter_not_supported',
  'budget_exhausted',
  'not_required',
];

function parseEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

/**
 * A link's role, or `context`.
 *
 * `context` rather than `supports` for anything unrecognised, and that direction
 * is the whole point: an unknown role must never be read as a claim that the
 * source backs the statement.
 */
export function parseClaimSourceRole(value: unknown): ClaimSourceRole {
  return parseEnum(value, CLAIM_SOURCE_ROLES, 'context');
}
